import React from 'react';
import type { Report, ReportsListLabels } from '../../shared/types/reports';
import type { ReportFilter } from '../../components/ReportsListPageContainer';
import { ReportCard } from './reports/ReportCard';

export interface ReportsListPageProps {
  reports: Report[];
  totalCount: number;
  page: number;
  perPage: number;
  hasMore: boolean;
  filter: ReportFilter;
  onFilterChange: (filter: ReportFilter) => void;
  onPageChange: (page: number) => void;
  getReportUrl: (reportId: string) => string;
  statsUrl: string;
  diaryUrl: string;
  labels: ReportsListLabels;
  isLoading: boolean;
}

export function ReportsListPage({
  reports,
  totalCount,
  page,
  perPage,
  hasMore,
  filter,
  onFilterChange,
  onPageChange,
  getReportUrl,
  statsUrl,
  diaryUrl,
  labels,
  isLoading,
}: ReportsListPageProps) {
  const tabs: { key: ReportFilter; label: string }[] = [
    { key: 'all', label: labels.allReports },
    { key: 'week', label: labels.weeklyReports },
    { key: 'month', label: labels.monthlyReports },
  ];

  return (
    <div className="reports-list-page">
      {/* Header with navigation */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 mb-0">{labels.title}</h1>
        <a href={statsUrl} className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-bar-chart me-1"></i>
          Stats
        </a>
      </div>

      {/* Filter tabs */}
      <ul className="nav nav-pills mb-4">
        {tabs.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              className={`nav-link ${filter === tab.key ? 'active' : ''}`}
              onClick={() => onFilterChange(tab.key)}
              type="button"
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && reports.length === 0 && (
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="bi bi-file-earmark-text text-muted" style={{ fontSize: '3rem' }}></i>
          </div>
          <h5 className="text-muted">{labels.noReports}</h5>
          <p className="text-muted mb-4">{labels.noReportsDescription}</p>
          <a href={diaryUrl} className="btn btn-primary">
            Start tracking
          </a>
        </div>
      )}

      {/* Reports list */}
      {!isLoading && reports.length > 0 && (
        <>
          <div className="row g-3">
            {reports.map((report) => (
              <div className="col-12 col-md-6 col-lg-4" key={report.id}>
                <ReportCard
                  report={report}
                  reportUrl={getReportUrl(report.id)}
                  labels={labels}
                />
              </div>
            ))}
          </div>

          {/* Pagination */}
          {(page > 1 || hasMore) && (
            <nav className="mt-4" aria-label="Reports pagination">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                  >
                    Previous
                  </button>
                </li>
                <li className="page-item disabled">
                  <span className="page-link">
                    Page {page} of {Math.ceil(totalCount / perPage)}
                  </span>
                </li>
                <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => onPageChange(page + 1)}
                    disabled={!hasMore}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default ReportsListPage;
